// Dev-only fixtures for the confirm-page auto check-in block, mounted by
// dev-preview.vue under /library/components. Shape: airobot-be StatusResponse
// (see airobot-be/docs/architecture.md, status-by-confirmcode) as of c5c8f64:
// every document hangs off the segments[].passengers[] cell it belongs to, and
// the flat flights[].boarding_passes[] and flight-level roster are gone.
// Trimmed further of the duplicated ids and the collapsed summary. Prefixes:
//   MOCK      — mirrored by a salabam/airobot-mocks JSON file
//   NON MOCK  — legal under the contract, not captured yet
//   DEGENERE  — the contract permits it, the component props do not

const OUTBOUND_REQUEST = 'req_a11b22c33d';
const RETURN_REQUEST = 'req_e44f55a66b';

// Casing is deliberately mixed: Amadeus returns uppercase, the FE renders
// whatever arrives.
const PAX_ONE = {
  passenger_id: 1,
  external_id: '1',
  first_name: 'LUIGI',
  last_name: 'FERRARO',
};

const PAX_TWO = {
  passenger_id: 2,
  external_id: '2',
  first_name: 'Sofia',
  last_name: 'Colombo',
};

const PAX_THREE = {
  passenger_id: 3,
  external_id: '3',
  first_name: 'Anna',
  last_name: 'DE LUCA',
};

// One document per format the vendor returned for a cell, in payload order.
// Only the wallet one carries a public_url in production.
const DOCUMENT_FORMATS = [
  { format: 'pdf', all_in_one: true },
  { format: 'google_wallet', url: 'https://pay.google.com/gp/v/save/' },
  { format: 'passkit' },
];

const ALL_FORMATS = DOCUMENT_FORMATS.map(({ format }) => format);

// example.invalid never resolves: the mock links are meant to be dead, and they
// exist so the preview can show the renderings production leaves url-less.
const document_url = (journey_id, format, url, all_urls) => {
  if (url) {
    return `${url}${journey_id}`;
  }
  if (all_urls) {
    return `https://example.invalid/bp/${journey_id}.${format}`;
  }
  return null;
};

const documents = (journey_id, formats = ALL_FORMATS, all_urls = false) =>
  DOCUMENT_FORMATS.filter(({ format }) => formats.includes(format)).map(
    ({ format, url = '', all_in_one = null }) => ({
      format,
      available: true,
      public_url: document_url(journey_id, format, url, all_urls),
      boarding_pass_sent_at: null,
      // Null in every real payload captured so far.
      seat_number: null,
      gate_close_time: null,
      is_all_in_one_pdf: all_in_one,
      is_all_in_one_segment: all_in_one,
    })
  );

// One (leg x traveler) cell. Defaults to pending with no document, the state
// the confirm page sees for most of the booking's life.
const cell = (pax, overrides = {}) => ({
  passenger_id: pax.passenger_id,
  external_id: pax.external_id,
  first_name: pax.first_name,
  last_name: pax.last_name,
  passenger_journey_id: null,
  status: 'pending',
  error: null,
  boarding_pass_available: false,
  boarding_passes: [],
  requires_apis: null,
  apis_url: null,
  ...overrides,
});

const issued = (pax, journey_id, overrides = {}) =>
  cell(pax, {
    passenger_journey_id: journey_id,
    status: 'success',
    boarding_pass_available: true,
    boarding_passes: documents(journey_id),
    ...overrides,
  });

const segment = (overrides = {}) => ({
  leg: 1,
  journey_id: 1,
  carrier: 'VY',
  flight_number: '6112',
  departure_airport: 'MXP',
  arrival_airport: 'BCN',
  // Local time at the airport, no UTC offset.
  departure_at: '2026-09-01T07:35:00',
  arrival_at: '2026-09-01T09:15:00',
  pnr: 'K8M2QR',
  passengers: [],
  ...overrides,
});

const return_segment = (overrides = {}) =>
  segment({
    journey_id: 2,
    flight_number: '6113',
    departure_airport: 'BCN',
    arrival_airport: 'MXP',
    departure_at: '2026-09-08T18:20:00',
    arrival_at: '2026-09-08T19:55:00',
    ...overrides,
  });

const flight = (overrides = {}) => ({
  request_id: OUTBOUND_REQUEST,
  status: 'pending',
  last_event_at: '2026-08-30 09:10:00',
  error: null,
  origin: 'MXP',
  destination: 'BCN',
  linked_request_id: null,
  segments: [
    segment({ passengers: [cell(PAX_ONE, { passenger_journey_id: 5501 })] }),
  ],
  ...overrides,
});

const return_flight = (overrides = {}) =>
  flight({
    request_id: RETURN_REQUEST,
    last_event_at: '2026-08-30 09:10:04',
    origin: 'BCN',
    destination: 'MXP',
    linked_request_id: OUTBOUND_REQUEST,
    segments: [
      return_segment({
        passengers: [cell(PAX_ONE, { passenger_journey_id: 5601 })],
      }),
    ],
    ...overrides,
  });

const checkin = (status_summary, flights = []) => ({
  status_summary,
  flights,
});

const two_pax_segment = (overrides = {}) =>
  segment({
    passengers: [cell(PAX_ONE), cell(PAX_TWO)],
    ...overrides,
  });

// Fictitious 7-leg routing: one leg per format, one per pair of formats, one
// with all three. Every boarding-pass rendering in a single request.
const FORMAT_MATRIX = [
  { route: ['MXP', 'BCN'], formats: ['pdf'] },
  { route: ['BCN', 'LIS'], formats: ['google_wallet'] },
  { route: ['LIS', 'MAD'], formats: ['passkit'] },
  { route: ['MAD', 'CDG'], formats: ['pdf', 'google_wallet'] },
  { route: ['CDG', 'AMS'], formats: ['pdf', 'passkit'] },
  { route: ['AMS', 'CPH'], formats: ['google_wallet', 'passkit'] },
  { route: ['CPH', 'MXP'], formats: ['pdf', 'google_wallet', 'passkit'] },
];

const MATRIX_PAX = [PAX_ONE, PAX_TWO, PAX_THREE];

const matrix_cell = (pax, journey_id, formats) =>
  issued(pax, journey_id, {
    boarding_passes: documents(journey_id, formats, true),
  });

const MATRIX_SEGMENTS = FORMAT_MATRIX.map(({ route, formats }, index) =>
  segment({
    leg: index + 1,
    journey_id: index + 1,
    flight_number: `90${index + 1}`,
    departure_airport: route[0],
    arrival_airport: route[1],
    departure_at: `2026-09-0${index + 1}T08:00:00`,
    arrival_at: `2026-09-0${index + 1}T10:30:00`,
    // Every traveller on a leg gets the same formats, as the vendor issues them.
    passengers: MATRIX_PAX.map((pax, pax_index) =>
      matrix_cell(pax, 8001 + index * 10 + pax_index, formats)
    ),
  })
);

export const CHECK_IN_SCENARIOS = [
  {
    key: 'not-requested',
    label: 'MOCK — not_requested — nessuna riga di richiesta',
    probe: 'status_summary: not_requested, flights: []',
    expectation:
      'In produzione non arriva mai: get_confirmation scarta il blocco quando il summary è not_requested (e inietta pending se airobotRequested). Qui passa dal ramo flights vuoti: una sola riga "Servizio non richiesto", nessun link.',
    checkin: checkin('not_requested', []),
  },
  {
    key: 'pending-one-way',
    label: 'MOCK — pending — sola andata, griglia già presente',
    probe: '1 leg, 1 cella pending, 0 documenti',
    expectation:
      'Una riga "Richiesta in attesa" senza etichetta itinerario (stato omogeneo) e senza link: le celle esistono prima dei documenti.',
    checkin: checkin('pending', [flight()]),
  },
  {
    key: 'success-one-way',
    label: 'MOCK — success — sola andata, 1 pax, carta pronta',
    probe: '1 leg success, 1 documento con public_url',
    expectation:
      '"Richiesta emessa" + un link con il nominativo dal backend. Nessuna etichetta di tratta: il gruppo è unico.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        segments: [segment({ passengers: [issued(PAX_ONE, 5501)] })],
      }),
    ]),
  },
  {
    key: 'success-round-trip',
    label: 'MOCK — success — andata/ritorno, 2 leg × 2 pax',
    probe: '2 richieste collegate, 4 documenti',
    expectation:
      'Due pannelli (uno per richiesta), due link ciascuno. linked_request_id lega le due direzioni.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        linked_request_id: RETURN_REQUEST,
        segments: [
          two_pax_segment({
            passengers: [issued(PAX_ONE, 6101), issued(PAX_TWO, 6102)],
          }),
        ],
      }),
      return_flight({
        status: 'success',
        segments: [
          return_segment({
            passengers: [issued(PAX_ONE, 6201), issued(PAX_TWO, 6202)],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'partial-success-round-trip',
    label: 'MOCK — partial_success — andata emessa, ritorno in attesa',
    probe: 'leg success + leg pending',
    expectation:
      'Stato non omogeneo: entrambe le righe visibili con etichetta (MXP - BCN) e (BCN - MXP) da origin/destination.',
    checkin: checkin('partial_success', [
      flight({
        status: 'success',
        linked_request_id: RETURN_REQUEST,
        segments: [segment({ passengers: [issued(PAX_ONE, 6101)] })],
      }),
      return_flight(),
    ]),
  },
  {
    key: 'failed-vendor-rejected',
    label: 'MOCK — failed — vendor rifiuta (solo message, nessun code)',
    probe: '1 leg failed, error.message senza error.code',
    expectation:
      '"Richiesta fallita" con la copy di assistenza. Il motivo resta nel payload: non è mostrato.',
    checkin: checkin('failed', [
      flight({
        status: 'failed',
        error: { message: 'Airobot rejected the check-in request' },
        segments: [
          segment({ passengers: [cell(PAX_ONE, { status: 'failed' })] }),
        ],
      }),
    ]),
  },
  {
    key: 'failed-polling-timeout',
    label: 'MOCK — failed — polling esaurito (polling_max_errors)',
    probe: '1 leg failed, error con code + message',
    expectation: 'Come sopra: la distinzione tra i due errori non è resa.',
    checkin: checkin('failed', [
      flight({
        status: 'failed',
        error: {
          code: 'polling_max_errors',
          message: 'Polling exhausted after 5 attempts',
        },
      }),
    ]),
  },
  {
    key: 'canceled-after-resync',
    label: 'MOCK — partial_success — leg annullato da un resync di orario',
    probe: 'leg canceled + leg success',
    expectation:
      'Il canceled rientra in partial_success (_derive_summary non ha un ramo dedicato) e usa la stessa resa di failed.',
    checkin: checkin('partial_success', [
      flight({ status: 'canceled' }),
      return_flight({
        status: 'success',
        segments: [return_segment({ passengers: [issued(PAX_ONE, 6201)] })],
      }),
    ]),
  },
  {
    key: 'format-matrix',
    label: 'NON MOCK — success — 7 tratte, una per combinazione di formati',
    probe: '3 pax su 7 tratte: 3 con un solo formato, 3 con due, 1 con tutti e tre',
    expectation:
      'Sette gruppi etichettati, tre righe passeggero ciascuno con la stessa combinazione: il link testuale del pdf e i due badge, da soli e affiancati. Rotta e link sono inventati. È anche il caso che il payload precedente non poteva esprimere: un unico request con più tratte.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        destination: 'MXP',
        segments: MATRIX_SEGMENTS,
      }),
    ]),
  },
  {
    key: 'interline-mixed-carrier',
    label: 'NON MOCK — success — interline, un PNR per tratta',
    probe: '2 segmenti con carrier e pnr diversi',
    expectation:
      'Le due tratte si distinguono solo dai segmenti: il PNR è per tratta, non per itinerario.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        destination: 'CDG',
        segments: [
          segment({
            carrier: 'FR',
            flight_number: '9014',
            pnr: 'X73V2E',
            passengers: [issued(PAX_ONE, 1001)],
          }),
          segment({
            leg: 2,
            journey_id: 2,
            carrier: 'VY',
            flight_number: '8422',
            departure_airport: 'BCN',
            arrival_airport: 'CDG',
            departure_at: '2026-09-25T12:30:00',
            arrival_at: '2026-09-25T14:20:00',
            pnr: 'Y3VBZ5',
            passengers: [issued(PAX_ONE, 1002)],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'boarding-pass-not-ready',
    label: 'NON MOCK — success — carta non ancora scaricabile',
    probe: 'cella success, boarding_pass_available false, public_url null',
    expectation:
      '"Richiesta emessa" senza alcun link: il gruppo senza documenti scaricabili non viene reso.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        segments: [
          segment({
            passengers: [
              cell(PAX_ONE, {
                passenger_journey_id: 5501,
                status: 'success',
              }),
            ],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'mixed-formats',
    label: 'NON MOCK — success — tutti e tre i formati scaricabili',
    probe: '3 documenti per pax: pdf, google_wallet, passkit',
    expectation:
      'Il pdf resta un link testuale, gli altri due sono i badge ufficiali. Link finti: servono a vedere le tre rese insieme.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        segments: [
          segment({
            passengers: [
              issued(PAX_ONE, 5501, {
                boarding_passes: documents(5501, ALL_FORMATS, true),
              }),
            ],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'passkit-only',
    label: 'NON MOCK — success — solo il passkit ha un URL',
    probe: '1 documento passkit, pdf e wallet senza URL',
    expectation:
      'Un solo badge Apple. Se add-to-apple-wallet.svg non è stato ancora messo in public/assets, ricade sul link testuale.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        segments: [
          segment({
            passengers: [
              issued(PAX_ONE, 5501, {
                boarding_passes: documents(5501, ['pdf', 'passkit']).map(
                  (document) => {
                    if (document.format !== 'passkit') {
                      return document;
                    }
                    return {
                      ...document,
                      public_url: 'https://example.invalid/bp/5501.pkpass',
                    };
                  }
                ),
              }),
            ],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'download-url-fallback',
    label: 'NON MOCK — success — pdf servito da download_url',
    probe: 'pdf senza public_url ma con download_url',
    expectation:
      'Il nome torna cliccabile: la resa legge public_url || download_url. Campo non ancora esposto dal backend.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        segments: [
          segment({
            passengers: [
              issued(PAX_ONE, 5501, {
                boarding_passes: documents(5501).map((document) => {
                  if (document.format !== 'pdf') {
                    return document;
                  }
                  return {
                    ...document,
                    download_url: 'https://example.invalid/bp/5501.pdf',
                  };
                }),
              }),
            ],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'public-url-null',
    label: 'NON MOCK — success — nessun formato scaricabile',
    probe: 'pdf e passkit available true, public_url null su entrambi',
    expectation:
      'Nessun link invece di un href="null". Servirebbe il proxy lf_checkinBoardingPass.php: request_id, passenger_journey_id e format sono tutti nel payload. Senza il wallet resta questo il caso.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        segments: [
          segment({
            passengers: [
              issued(PAX_ONE, 5501, {
                boarding_passes: documents(5501, ['pdf', 'passkit']),
              }),
            ],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'requires-apis',
    label: 'NON MOCK — pending — servono i dati documento (APIS)',
    probe: 'cella pending con requires_apis + apis_url',
    expectation:
      'Oggi indistinguibile da una normale attesa: apis_url non è reso, la copy si limita a "potresti ricevere un\'email".',
    checkin: checkin('pending', [
      flight({
        segments: [
          segment({
            passengers: [
              cell(PAX_ONE, {
                passenger_journey_id: 5501,
                requires_apis: true,
                apis_url: 'https://demo.instago.click/apis/req_a11b22c33d/1',
              }),
            ],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'no-vendor-identity',
    label: 'NON MOCK — success — nominativi assenti sulla cella',
    probe: 'first_name e last_name null, documento presente',
    expectation: 'Fallback posizionale "Boarding Pass 1" / "Boarding Pass 2".',
    checkin: checkin('success', [
      flight({
        status: 'success',
        segments: [
          two_pax_segment({
            passengers: [
              issued(PAX_ONE, 6101, { first_name: null, last_name: null }),
              issued(PAX_TWO, 6102, { first_name: null, last_name: null }),
            ],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'segments-empty-create-failed',
    label: 'NON MOCK — failed — create mai risposta, nessuna tratta nota',
    probe: 'segments: [], nessun documento',
    expectation:
      "Senza il roster non resta traccia dei passeggeri: la riga mostra solo lo stato e l'etichetta (MXP - BCN) da origin/destination.",
    checkin: checkin('failed', [
      flight({
        status: 'failed',
        error: { message: 'Airobot create call never answered' },
        segments: [],
      }),
    ]),
  },
  {
    key: 'mixed-pax-single-leg',
    label: "NON MOCK — partial_success — un pax imbarcato, l'altro no",
    probe: '1 leg, 2 celle con status diverso',
    expectation:
      'Un solo link: la cella pending non ha documento. Lo stato per-passeggero non è ancora reso.',
    checkin: checkin('partial_success', [
      flight({
        status: 'success',
        segments: [
          two_pax_segment({
            passengers: [issued(PAX_ONE, 6101), cell(PAX_TWO)],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'multi-city-three-legs',
    label: 'NON MOCK — success — multi-city, 3 tratte',
    probe: '3 segmenti, 1 documento ciascuno',
    expectation: 'Tre gruppi etichettati, un link per gruppo.',
    checkin: checkin('success', [
      flight({
        status: 'success',
        destination: 'MXP',
        segments: [
          segment({ passengers: [issued(PAX_ONE, 7001)] }),
          segment({
            leg: 2,
            journey_id: 2,
            flight_number: '8710',
            departure_airport: 'BCN',
            arrival_airport: 'LIS',
            departure_at: '2026-09-05T10:05:00',
            arrival_at: '2026-09-05T11:40:00',
            passengers: [issued(PAX_ONE, 7002)],
          }),
          segment({
            leg: 3,
            journey_id: 3,
            carrier: 'TP',
            flight_number: '830',
            departure_airport: 'LIS',
            arrival_airport: 'MXP',
            departure_at: '2026-09-10T07:15:00',
            arrival_at: '2026-09-10T11:00:00',
            passengers: [issued(PAX_ONE, 7003)],
          }),
        ],
      }),
    ]),
  },
  {
    key: 'unknown-leg-status',
    label: 'DEGENERE — stato leg fuori dall\'enum ("waiting-documents")',
    probe: 'status non previsto',
    expectation:
      'Ricade su pending. airobot-be normalizza a monte, quindi non dovrebbe arrivare.',
    checkin: checkin('pending', [flight({ status: 'waiting-documents' })]),
  },
  {
    key: 'null-summary-no-flights',
    label: 'DEGENERE — status_summary null e flights vuoti',
    probe: 'payload privo di informazione',
    expectation:
      'La prop status_summary ricade sul default not_requested del componente.',
    checkin: checkin(null, []),
  },
];
